import type { CSSProperties, FC } from 'react';
import { useMemo } from 'react';
import { TbChartDots2 } from 'react-icons/tb';

import { useReportComponentAccess } from '@/hooks/useNestedResourceAccess';
import type { ContextualCommentTarget, ReportComponent, ResourceComment } from '@/types';

import ContextualCommentButton from '../ContextualCommentButton';
import {
    getReportComponentCommentResource,
    ReportComponentCommentResourceContext,
    reportComponentTitle,
} from './resource';

export type { ReportComponentCommentResourceContext } from './resource';

interface ReportComponentCommentButtonProps {
    buttonStyle?: CSSProperties;
    component: ReportComponent;
    commentResource?: ReportComponentCommentResourceContext;
    disabled?: boolean;
    onCommentCreated?: (comment: ResourceComment) => void;
    tooltipPlacement?: 'top' | 'topLeft' | 'topRight' | 'bottom' | 'bottomLeft' | 'bottomRight';
}

const getDashboardIdFromLocation = () => {
    if (typeof window === 'undefined') {
        return undefined;
    }

    return window.location.pathname.match(/^\/dashboards\/([^/]+)/)?.[1];
};

const ReportComponentCommentButton: FC<ReportComponentCommentButtonProps> = ({
    buttonStyle,
    commentResource,
    component,
    disabled,
    onCommentCreated,
    tooltipPlacement = 'top',
}) => {
    const reportAccess = useReportComponentAccess(component);
    const title = reportComponentTitle(component);
    const dashboardIdFromLocation = getDashboardIdFromLocation();
    const dashboard = component.report?.dashboard;
    const fallbackResource = useMemo(
        () => getReportComponentCommentResource(component, dashboardIdFromLocation),
        [component, dashboardIdFromLocation],
    );
    const resource = commentResource ?? fallbackResource;
    const target = useMemo<ContextualCommentTarget>(
        () => ({
            component: resource.component ?? `reportComponent:${component.id}`,
            label: title,
            metadata: {
                dashboardId: dashboard?.id ?? dashboardIdFromLocation,
                reportComponentId: component.id,
                reportComponentName: title,
                reportComponentType: component.type,
                reportId: component.report?.id,
                source: 'report-component-header',
                ...(resource.metadata ?? {}),
                target: {
                    componentId: component.id,
                    componentType: component.type,
                    label: title,
                    nodeType: 'reportComponent',
                    reportId: component.report?.id,
                    type: 'Report Component',
                    ...((resource.metadata?.target as Record<string, unknown> | undefined) ?? {}),
                },
            },
            type: component.type ?? 'Report Component',
        }),
        [component.id, component.report?.id, component.type, dashboard?.id, dashboardIdFromLocation, resource, title],
    );

    return (
        <ContextualCommentButton
            buttonStyle={buttonStyle}
            buttonType='default'
            disabled={disabled || reportAccess.comment.disabled || !component.id}
            icon={<TbChartDots2 />}
            onCommentCreated={onCommentCreated}
            resource={resource}
            target={target}
            tooltipPlacement={tooltipPlacement}
        />
    );
};

export default ReportComponentCommentButton;
